import type { SummaryPayload } from "../../lib/auth";

type SeriesPoint = SummaryPayload["series"][number];

type ChartProps = {
  series: SeriesPoint[];
};

const chartWidth = 760;
const chartHeight = 240;
const margin = {
  top: 18,
  right: 16,
  bottom: 42,
  left: 42,
};

function formatLabel(date: string) {
  return new Date(`${date}T00:00:00.000Z`).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
}

function toPercent(value: number | null) {
  if (value === null) {
    return 0;
  }
  return Math.max(0, Math.min(100, value * 100));
}

function buildTicks(maxValue: number, tickCount = 4) {
  if (maxValue <= 0) {
    return [0];
  }
  const step = Math.max(1, Math.ceil(maxValue / tickCount));
  const ticks: number[] = [];
  for (let value = 0; value <= maxValue; value += step) {
    ticks.push(value);
  }
  if (ticks[ticks.length - 1] !== maxValue) {
    ticks.push(maxValue);
  }
  return ticks;
}

export function DailyVolumeChart({ series }: ChartProps) {
  const plotWidth = chartWidth - margin.left - margin.right;
  const plotHeight = chartHeight - margin.top - margin.bottom;
  const maxValue = Math.max(
    ...series.map((point) => Math.max(point.total, point.open, point.blockers)),
    1
  );
  const y = (value: number) => margin.top + plotHeight - (value / maxValue) * plotHeight;
  const groupWidth = plotWidth / Math.max(series.length, 1);
  const barGap = 2;
  const barWidth = Math.max(4, Math.min(14, (groupWidth - barGap * 2) / 3));
  const ticks = buildTicks(maxValue);
  const labelEvery = Math.max(1, Math.ceil(series.length / 8));

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
        <span className="inline-flex items-center gap-1">
          <span className="h-2.5 w-2.5 rounded-sm bg-foreground/80" />
          Total
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="h-2.5 w-2.5 rounded-sm bg-sky-400" />
          Open
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="h-2.5 w-2.5 rounded-sm bg-amber-400" />
          Blockers
        </span>
      </div>
      <svg
        viewBox={`0 0 ${chartWidth} ${chartHeight}`}
        className="h-64 w-full overflow-visible rounded-lg border border-border/60 bg-muted/20"
        role="img"
        aria-label="Daily volume chart"
      >
        {ticks.map((tick) => {
          const yPos = y(tick);
          return (
            <g key={tick}>
              <line
                x1={margin.left}
                x2={chartWidth - margin.right}
                y1={yPos}
                y2={yPos}
                stroke="currentColor"
                strokeOpacity={0.12}
              />
              <text
                x={margin.left - 8}
                y={yPos + 4}
                textAnchor="end"
                className="fill-muted-foreground text-[10px]"
              >
                {tick}
              </text>
            </g>
          );
        })}
        {series.map((point, index) => {
          const groupStart = margin.left + index * groupWidth + (groupWidth - (barWidth * 3 + 4)) / 2;
          const xTotal = groupStart;
          const xOpen = groupStart + barWidth + barGap;
          const xBlockers = groupStart + (barWidth + barGap) * 2;
          const showLabel = index % labelEvery === 0 || index === series.length - 1;
          return (
            <g key={point.date}>
              <rect
                x={xTotal}
                y={y(point.total)}
                width={barWidth}
                height={margin.top + plotHeight - y(point.total)}
                className="fill-foreground/80"
                rx={1}
              />
              <rect
                x={xOpen}
                y={y(point.open)}
                width={barWidth}
                height={margin.top + plotHeight - y(point.open)}
                className="fill-sky-400"
                rx={1}
              />
              <rect
                x={xBlockers}
                y={y(point.blockers)}
                width={barWidth}
                height={margin.top + plotHeight - y(point.blockers)}
                className="fill-amber-400"
                rx={1}
              />
              {showLabel ? (
                <text
                  x={groupStart + (barWidth * 3 + 4) / 2}
                  y={chartHeight - 10}
                  textAnchor="middle"
                  className="fill-muted-foreground text-[10px]"
                >
                  {formatLabel(point.date)}
                </text>
              ) : null}
            </g>
          );
        })}
      </svg>
    </div>
  );
}

export function DailyRateChart({ series }: ChartProps) {
  const rateSeries = series.map((point) => {
    const slaRate =
      point.slaEligible > 0 ? point.slaCompliant / point.slaEligible : null;
    return {
      date: point.date,
      slaRate: toPercent(slaRate),
    };
  });

  const plotWidth = chartWidth - margin.left - margin.right;
  const plotHeight = chartHeight - margin.top - margin.bottom;
  const groupWidth = plotWidth / Math.max(rateSeries.length, 1);
  const barWidth = Math.max(5, Math.min(18, groupWidth));
  const y = (value: number) => margin.top + plotHeight - (value / 100) * plotHeight;
  const ticks = [0, 25, 50, 75, 100];
  const labelEvery = Math.max(1, Math.ceil(rateSeries.length / 8));

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
        <span className="inline-flex items-center gap-1">
          <span className="h-2.5 w-2.5 rounded-sm bg-emerald-400" />
          SLA Compliance %
        </span>
      </div>
      <svg
        viewBox={`0 0 ${chartWidth} ${chartHeight}`}
        className="h-64 w-full overflow-visible rounded-lg border border-border/60 bg-muted/20"
        role="img"
        aria-label="Daily SLA compliance chart"
      >
        {ticks.map((tick) => {
          const yPos = y(tick);
          return (
            <g key={tick}>
              <line
                x1={margin.left}
                x2={chartWidth - margin.right}
                y1={yPos}
                y2={yPos}
                stroke="currentColor"
                strokeOpacity={0.12}
              />
              <text
                x={margin.left - 8}
                y={yPos + 4}
                textAnchor="end"
                className="fill-muted-foreground text-[10px]"
              >
                {tick}
              </text>
            </g>
          );
        })}
        {rateSeries.map((point, index) => {
          const groupStart = margin.left + index * groupWidth + (groupWidth - barWidth) / 2;
          const showLabel = index % labelEvery === 0 || index === rateSeries.length - 1;
          return (
            <g key={point.date}>
              <rect
                x={groupStart}
                y={y(point.slaRate)}
                width={barWidth}
                height={margin.top + plotHeight - y(point.slaRate)}
                className="fill-emerald-400"
                rx={1}
              />
              {showLabel ? (
                <text
                  x={groupStart + barWidth / 2}
                  y={chartHeight - 10}
                  textAnchor="middle"
                  className="fill-muted-foreground text-[10px]"
                >
                  {formatLabel(point.date)}
                </text>
              ) : null}
            </g>
          );
        })}
      </svg>
    </div>
  );
}
